/**
 * Canonical map color palettes (CLARID-UX-2026-06, PR 8 — mapping foundation).
 *
 * Single source of truth for the colors the three maps currently define inline
 * and inconsistently. The income-tier palette follows the **Fair Lending /
 * Wallace 2026-04-28 spec** (the authoritative one): the CRA assessment-area map
 * currently has MIDDLE/UPPER swapped — PR 9 reconciles it to this when it moves
 * onto the shared engine. No page consumes this yet (foundation only).
 */

/**
 * CRA tract income tiers — COLORBLIND-SAFE viridis ramp. Ordered LOW→UPPER and,
 * crucially, separated by LIGHTNESS (dark → light), which every type of color
 * vision perceives — so red-green colorblind users (~8% of men, examiners
 * included) can still tell the tiers apart. The earlier red/orange/yellow/green
 * scheme failed exactly this (those hues collapse together under red-green CVD).
 * Design is ours — Michael validated the data/output, not the colors. Typed as a
 * string map so consumers can index it by a dynamic level without casting.
 */
export const INCOME_COLORS: Record<string, string> = {
  LOW: '#440154', // viridis — dark purple (LMI stands out)
  MODERATE: '#3b528b', // viridis — indigo
  MIDDLE: '#21918c', // viridis — teal
  UPPER: '#5ec962', // viridis — green (lightest)
  UNKNOWN: '#9ca3af', // gray-400
};

export const INCOME_LABELS: Record<string, string> = {
  LOW: 'Low Income',
  MODERATE: 'Moderate Income',
  MIDDLE: 'Middle Income',
  UPPER: 'Upper Income',
  UNKNOWN: 'Unknown',
};

export type IncomeLevel = 'LOW' | 'MODERATE' | 'MIDDLE' | 'UPPER' | 'UNKNOWN';

/**
 * Majority-minority census tract shading (regulator-aware label comes from the
 * API). MMCT is a bold violet — distinct from the income-tier reds so the two
 * layers never read the same — against a light neutral so MMCTs pop.
 */
export const MMCT_COLORS = {
  MMCT: '#7c3aed', // violet-600
  NON_MMCT: '#e2e8f0', // slate-200
} as const;

/** HMDA action_taken colors for loan dots. */
export const ACTION_COLORS: Record<string, string> = {
  '1': '#16a34a', // Originated — green
  '2': '#84cc16', // Approved not accepted — lime
  '3': '#dc2626', // Denied — red
  '4': '#94a3b8', // Withdrawn — slate
  '5': '#94a3b8', // Closed for incompleteness — slate
  '6': '#3b82f6', // Purchased — blue
  '7': '#dc2626', // Preapproval denied — red
  '8': '#84cc16', // Preapproval approved not accepted — lime
};

/** Bond property markers by CRA-qualification status. */
export const BOND_STATUS_COLORS = {
  qualifies: '#2563eb', // blue-600 — colorblind-safe "positive" (green confused with red/amber for CVD viewers)
  outside_aa: '#6b7280', // gray-500 — neutral
  no_affordability: '#f59e0b', // amber-500 — caution / missing data
  no_geocode: '#dc2626', // red-600 — geocode failure (error)
} as const;

export const BOND_STATUS_LABELS = {
  qualifies: 'Qualifies for CRA',
  outside_aa: 'Outside bank AAs',
  no_affordability: 'No affordability data',
  no_geocode: 'Geocoding failed',
} as const;

/** Small-business density — sequential cyan quartile ramp. */
export const SB_DENSITY_RAMP = ['#cffafe', '#67e8f9', '#06b6d4', '#0e7490'] as const; // cyan 100/300/500/700
export const NO_DATA_FILL = '#e5e7eb'; // gray-200

/** Bucket a value into one of four quartile colors given the bin edges. */
export function sbDensityColor(
  value: number,
  bins: { q1: number; q2: number; q3: number },
): string {
  if (value < bins.q1) return SB_DENSITY_RAMP[0];
  if (value < bins.q2) return SB_DENSITY_RAMP[1];
  if (value < bins.q3) return SB_DENSITY_RAMP[2];
  return SB_DENSITY_RAMP[3];
}

/**
 * Credit-penetration diverging scale — a tract's loans-per-SB relative to the AA
 * mean (1.0 = at the AA average). COLORBLIND-SAFE orange↔teal diverging: under-
 * served = warm orange, ~average = light neutral, over-served = cool teal. The
 * prior red→yellow→green scale was the classic red-green CVD trap (under- and
 * over-served collapsed together — defeating the whole point of the view).
 */
export function creditAccessColor(relative: number, hasLoans: boolean): string {
  if (!hasLoans) return '#7c2d12'; // orange-900 — no loans at all (most under-served)
  if (relative < 0.5) return '#c2410c'; // orange-700 — severely under
  if (relative < 0.9) return '#fb923c'; // orange-400 — under
  if (relative < 1.1) return '#f1f5f9'; // slate-100 — ~ average (neutral midpoint)
  if (relative < 1.5) return '#5eead4'; // teal-300 — over
  return '#0f766e'; // teal-700 — well over-served
}

/**
 * Mapbox `match` paint expression coloring a fill by tract income level.
 * Used by the shared engine's income layer (and by pages migrating onto it).
 */
export function incomePaintExpression(property = 'incomeLevel'): unknown[] {
  return [
    'match',
    ['get', property],
    'LOW', INCOME_COLORS.LOW,
    'MODERATE', INCOME_COLORS.MODERATE,
    'MIDDLE', INCOME_COLORS.MIDDLE,
    'UPPER', INCOME_COLORS.UPPER,
    INCOME_COLORS.UNKNOWN,
  ];
}
